/**
 * @license
 * @copyright Copyright Motili Inc., 2017 All Rights Reserved
 */

import moment from 'moment-timezone';
import merge from 'lodash/merge';
import * as ApiService from './ApiService';

const baseUrl = 'billings';

export async function findWithCount(processedFilters) {
    const filterFields = {
        filter: {
            include: ['client'],
        },
    };
    const query = merge({}, filterFields, { filter: processedFilters });
    const invoices = await ApiService.get(true, `${baseUrl}/invoices`, query);
    const countResp = await ApiService.get(
        true,
        `${baseUrl}/invoices/count`,
        query
    );

    return { invoices, count: countResp.count };
}

export function downloadInvoice(
    ids,
    clientId,
    orderedOnBehalf,
    downloadForClient = false
) {
    const query = {
        clientId,
        orderedOnBehalf: !!orderedOnBehalf,
        downloadForClient: !!downloadForClient,
    };

    return ApiService.post(true, `${baseUrl}/invoices/download`, ids, query);
}

export function findDownloadLog(query) {
    return ApiService.get(true, `${baseUrl}/invoices/downloadLogs`, query);
}

export function countDownloadLog(query) {
    return ApiService.get(
        true,
        `${baseUrl}/invoices/downloadLogs/count`,
        query
    );
}

export function processFilters(searchFilters, advancedFilters, activeQuery) {
    const query = merge({}, activeQuery, {
        where: {
            and: [],
        },
    });

    if (
        !Object.keys(searchFilters).length &&
        !Object.keys(advancedFilters).length
    ) {
        return query;
    }

    // Search filter section
    const filterGroups = {};

    Object.keys(searchFilters).forEach(key => {
        filterGroups[key] = { or: [] };

        switch (key) {
            case 'INVOICE_NUMBER':
                filterGroups[key].or.push(
                    ...searchFilters[key].map(filter => {
                        return {
                            invoiceNumber: {
                                like: `%${filter.term}%`,
                                options: 'i',
                            },
                        };
                    })
                );
                break;
            case 'PO_NUMBER':
                filterGroups[key].or.push(
                    ...searchFilters[key].map(filter => {
                        return {
                            customerPoNumber: {
                                like: `%${filter.term}%`,
                            },
                        };
                    })
                );
                break;
            default:
        }
    });

    Object.values(filterGroups).forEach(group => {
        if (group.or.length > 0) {
            query.where.and.push(group);
        }
    });

    // Advanced filter section
    const orderedOnBehalfClientIds = { or: [] };
    const payeeClientIds = { or: [] };
    const invoiceStatus = { or: [] };

    Object.keys(advancedFilters).forEach(key => {
        switch (key) {
            case 'invoiceStatus':
                if (
                    advancedFilters[key]
                        .map(item => item.term)
                        .includes('INVOICE_DELETED')
                ) {
                    invoiceStatus.or.push({
                        state: 'DELETED',
                    });
                }
                if (
                    advancedFilters[key]
                        .map(item => item.term)
                        .includes('INVOICE_READY')
                ) {
                    invoiceStatus.or.push({
                        downloaded: false,
                        state: { neq: 'DELETED' },
                    });
                }
                if (
                    advancedFilters[key]
                        .map(item => item.term)
                        .includes('INVOICE_DOWNLOADED')
                ) {
                    invoiceStatus.or.push({
                        downloaded: true,
                        state: { neq: 'DELETED' },
                    });
                }
                break;
            case 'orderedOnBehalfClientId':
                orderedOnBehalfClientIds.or.push({
                    orderedOnBehalfClientId: {
                        inq: advancedFilters[key].map(item => item.term),
                    },
                });
                break;
            case 'payeeClientId':
                payeeClientIds.or.push({
                    clientId: {
                        inq: advancedFilters[key].map(item => item.term),
                    },
                });
                break;
            case 'lackingSerial':
                query.where.and.push({
                    missingSerial: true,
                });
                break;
            case 'amountGreater':
                query.where.and.push({
                    amountTotal: {
                        gt: advancedFilters[key].term,
                    },
                });
                break;
            case 'amountLess':
                query.where.and.push({
                    amountTotal: {
                        lt: advancedFilters[key].term,
                    },
                });
                break;
            case 'dueDateBefore':
                query.where.and.push({
                    dueDate: {
                        lt: moment(advancedFilters[key].term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'dueDateAfter':
                query.where.and.push({
                    dueDate: {
                        gt: moment(advancedFilters[key].term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'invoiceDateBefore':
                query.where.and.push({
                    invoiceDate: {
                        lt: moment(advancedFilters[key].term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'invoiceDateAfter':
                query.where.and.push({
                    invoiceDate: {
                        gt: moment(advancedFilters[key].term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'updatedBefore':
                query.where.and.push({
                    updated: {
                        lt: moment(advancedFilters[key].term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'updatedAfter':
                query.where.and.push({
                    updated: {
                        gt: moment(advancedFilters[key].term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            default:
        }
    });

    query.where.and.push(
        orderedOnBehalfClientIds,
        payeeClientIds,
        invoiceStatus
    );

    return query;
}

export const getContractorInvoiceAccess = contractorId => {
    return ApiService.get(
        true,
        `${baseUrl}/contractorInvoiceAccess/${contractorId}`
    );
};
